import { useState, useEffect, useRef, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type DefaultValues } from "react-hook-form";
import { z } from "zod";
import { useTranslation } from "@/hooks/useTranslation";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/useToast";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useOrgNavigate } from "@/hooks/useOrgNavigate";
import { useProjects } from "@/hooks/useProjects";
import { useBusinesses } from "@/hooks/useBusinesses";
import { useLocationOptions } from "@/hooks/useLocations";
import { useCreateAsset, useUpdateAsset } from "@/hooks/useAssets";
import {
  useAssetAssignmentHistory,
  useAssignAssets,
  useReturnAssets,
  toastAssignmentError,
} from "@/hooks/useAssetAssignments";
import { formatLocalISODate, parseLocalISODate } from "@/lib/dateUtils";
import { useAssetAttachments, useUploadAssetAttachments, useDeleteAssetAttachment, type AssetAttachmentType } from "@/hooks/useAssetAttachments";
import { FileAttachments, type FileAttachment } from "@/components/ui/file-attachments";
import { ExpenseLinkingSection } from "./ExpenseLinkingSection";
import { useCurrentUserOrgRole } from "@/hooks/useCurrentUserOrgRole";
import { DualScopeSelect } from "@/components/common/DualScopeSelect";
import { useEndOfLifePlans, requiresWasteMethod } from "@/hooks/useEndOfLifePlans";
import { UnitSelect } from "@/components/materials/UnitSelect";
import type { AssignItem } from "@releve/shared";
import type { CreateAssetData, UpdateAssetData, AssetStatus } from "@/types/assets";

// Radix Select can't hold an empty value, so "No project" is a sentinel the
// field maps back to "".
const NO_PROJECT = "__no_project__";

// Dynamic schema based on context. The asset carries its own facts (incl.
// recycled content) plus five per-use DEFAULTS — unit, procurement, end of
// life, waste treatment, notes — copied into each new project use when it is
// assigned. Quantity is a fact of a use only, so it exists only in purchase
// mode (launched from a project), where it is sent with the assign call.
const createFormSchema = (isPurchaseMode: boolean) => {
  const baseSchema = {
    name: z.string().min(1, "Name is required"),
    description: z.string().optional(),
    asset_type_id: z.string().min(1, "Asset type is required"),
    location_id: z.string().optional(),
    purchase_expense_id: z.string().optional(),
    purchase_line_item_id: z.string().optional(),
    recycled_content_id: z.string().optional(),
    // Physical weight in kg (a fact of the thing). The NumberInput emits ""
    // when cleared — map it (and null) to undefined before the number check.
    weight_kg: z.preprocess(
      (v) => (v === "" || v === null || v === undefined ? undefined : Number(v)),
      // numeric(10,2) CHECK (>= 0) on the column.
      z.number().nonnegative().max(99_999_999.99).optional(),
    ),
    // The thing's per-use defaults (every mode).
    procurement_type_id: z.string().optional(),
    end_of_life_plan_id: z.string().optional(),
    waste_treatment_method_id: z.string().optional(),
    unit_code: z.string().optional(),
    sustainability_notes: z.string().optional(),
  };

  if (isPurchaseMode) {
    return z.object({
      ...baseSchema,
      purchase_value: z.string().min(1, "Purchase value is required"),
      current_value: z.string().optional(),
      purchase_date: z.date({ required_error: "Purchase date is required" }),
      business_id: z.string().optional(),
      quantity: z.string().min(1, "Quantity is required"),
      // Per-use only (the project assignment) — see above.
      quantity_used: z.string().optional(),
    });
  } else {
    return z.object({
      ...baseSchema,
      status: z.enum(["available", "in_use", "maintenance", "retired"]),
      purchase_value: z.string().optional(),
      current_value: z.string().optional(),
      purchase_date: z.date().optional(),
      // The project to put it on — applied through the assignment API after
      // the save (assign / transfer / return), never written to the asset.
      assigned_project_id: z.string().optional(),
      business_id: z.string().optional(),
    });
  }
};

interface CreateAssetFormProps {
  onSuccess?: () => void;
  onSubmitReady?: (submitHandler: () => void, isLoading: boolean) => void;
  /** Surfaces form dirtiness (incl. staged attachments) so the dialog can gate its close confirmation. */
  onDirtyChange?: (isDirty: boolean) => void;
  projectId?: string; // If provided, switches to purchase mode
  // Edit mode - if asset provided, we're editing
  asset?: {
    id: string;
    name: string;
    code?: string;
    description?: string;
    asset_type_id: string;
    status: string;
    purchase_value?: number;
    current_value?: number;
    purchase_date?: string;
    location_id?: string;
    assigned_project_id?: string;
    business_id?: string;
    purchase_expense_id?: string;
    purchase_line_item_id?: string;
    organization_id: string;
    recycled_content_id?: string;
    weight_kg?: number | null;
    // The thing's per-use defaults
    unit_code?: string;
    procurement_type_id?: string;
    end_of_life_plan_id?: string;
    waste_treatment_method_id?: string;
    sustainability_notes?: string;
  };
}

// All fields the form can hold across its three modes (mode-specific ones optional).
// Lets onSubmit read off the dynamic per-mode schema's data without `any`.
interface AssetFormValues {
  name: string;
  description?: string;
  asset_type_id: string;
  location_id?: string;
  status?: AssetStatus;
  purchase_value?: string;
  current_value?: string;
  purchase_date?: Date;
  assigned_project_id?: string;
  business_id?: string;
  quantity?: string;
  quantity_used?: string;
  purchase_expense_id?: string;
  purchase_line_item_id?: string;
  procurement_type_id?: string;
  recycled_content_id?: string;
  weight_kg?: number;
  end_of_life_plan_id?: string;
  waste_treatment_method_id?: string;
  unit_code?: string;
  sustainability_notes?: string;
}

export function CreateAssetForm({
  onSuccess,
  onSubmitReady,
  onDirtyChange,
  projectId,
  asset,
}: CreateAssetFormProps) {
  const { t } = useTranslation();
  const { can } = useCurrentUserOrgRole();
  const canLinkExpense = can("accounting");
  const isEditMode = !!asset;
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [attachmentTypes, setAttachmentTypes] = useState<Record<string, AssetAttachmentType>>({});
  const { selectedOrganization } = useOrganization();
  // Reference-data lists for the pickers — cached hooks. The dialog is conditionally
  // mounted, so these only fetch while it's open and are deduped across reopens.
  const { data: locations = [] } = useLocationOptions(selectedOrganization?.id);
  const { data: projects = [] } = useProjects(selectedOrganization?.id);
  const { data: businesses = [] } = useBusinesses(selectedOrganization?.id);
  const orgNavigate = useOrgNavigate();
  const createAssetMutation = useCreateAsset();
  const updateAssetMutation = useUpdateAsset();
  const uploadAttachments = useUploadAssetAttachments();
  const deleteAttachment = useDeleteAssetAttachment();
  const { data: existingAttachments } = useAssetAttachments(asset?.id);
  // LCA taxonomy lookup — needed for the conditional waste-treatment-method
  // field (shown only when selected EOL plan has `requires_waste_method=true`).
  // Cached 5min via useDualScopeTypes; no refetch storms on dialog reopen.
  const { data: endOfLifePlans } = useEndOfLifePlans(selectedOrganization?.id);
  const assignAssets = useAssignAssets();
  const returnAssets = useReturnAssets();
  // Edit mode: the asset's CURRENT project is its open assignment (the
  // project select starts there and is disabled until it has loaded).
  const { data: assignmentHistory, isLoading: historyLoading } = useAssetAssignmentHistory(
    selectedOrganization?.id,
    asset?.id,
  );
  const openAssignment = useMemo(
    () => assignmentHistory?.find((row) => row.returned_on === null) ?? null,
    [assignmentHistory],
  );

  const isPurchaseMode = !!projectId && !isEditMode;
  const formSchema = createFormSchema(isPurchaseMode);
  type FormData = z.infer<typeof formSchema>;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: isEditMode ? {
      name: asset.name,
      description: asset.description || "",
      asset_type_id: asset.asset_type_id,
      status: asset.status as AssetStatus,
      location_id: asset.location_id || "",
      // Seeded from the pointer; re-seeded from the open assignment once the
      // history loads (the effect below).
      assigned_project_id: asset.assigned_project_id || "",
      business_id: asset.business_id || "",
      purchase_expense_id: asset.purchase_expense_id || "",
      purchase_line_item_id: asset.purchase_line_item_id || "",
      purchase_value: asset.purchase_value?.toString() || "",
      current_value: asset.current_value?.toString() || "",
      purchase_date: asset.purchase_date ? parseLocalISODate(asset.purchase_date) ?? undefined : undefined,
      recycled_content_id: asset.recycled_content_id || "",
      weight_kg: asset.weight_kg ?? undefined,
      procurement_type_id: asset.procurement_type_id || "",
      end_of_life_plan_id: asset.end_of_life_plan_id || "",
      waste_treatment_method_id: asset.waste_treatment_method_id || "",
      unit_code: asset.unit_code || "",
      sustainability_notes: asset.sustainability_notes || "",
    } as DefaultValues<FormData> : isPurchaseMode ? {
      name: "",
      description: "",
      asset_type_id: "",
      location_id: "",
      purchase_value: "",
      current_value: "",
      purchase_date: new Date(),
      business_id: "",
      quantity: "1",
      quantity_used: "",
      procurement_type_id: "",
      recycled_content_id: "",
      weight_kg: undefined,
      end_of_life_plan_id: "",
      waste_treatment_method_id: "",
      unit_code: "",
      sustainability_notes: "",
    } : {
      name: "",
      description: "",
      asset_type_id: "",
      location_id: "",
      status: "available",
      purchase_expense_id: "",
      purchase_line_item_id: "",
      purchase_value: "",
      current_value: "",
      assigned_project_id: "",
      business_id: "",
      recycled_content_id: "",
      weight_kg: undefined,
      procurement_type_id: "",
      end_of_life_plan_id: "",
      waste_treatment_method_id: "",
      unit_code: "",
      sustainability_notes: "",
    },
  });

  // Edit mode: once the history lands, the project select shows the open
  // assignment's project (the source of truth), unless the user already
  // changed it.
  useEffect(() => {
    if (!isEditMode || historyLoading) return;
    if (form.getFieldState("assigned_project_id").isDirty) return;
    form.resetField("assigned_project_id", {
      defaultValue: openAssignment?.project_id ?? "",
    });
  }, [isEditMode, historyLoading, openAssignment, form]);

  // Holds the latest onSubmit so the handler registered with the parent never goes stale
  // (onSubmit closes over attachments/attachmentTypes — non-RHF state). Reassigned each render.
  const onSubmitRef = useRef<((data: FormData) => Promise<void>) | undefined>(undefined);

  // Expose submit handler to parent — calls the LATEST onSubmit via the ref, so attachments
  // added after the dialog opened are seen at submit time.
  useEffect(() => {
    onSubmitReady?.(() => form.handleSubmit((d) => onSubmitRef.current?.(d))(), loading);
  }, [loading, onSubmitReady, form]);

  // Surface dirtiness to the dialog so a pristine form closes without a "Discard changes?"
  // prompt. Read isDirty during render (establishes the RHF proxy subscription); staged-but-
  // unsaved attachments count as dirty (they live outside RHF state).
  const isFormDirty = form.formState.isDirty;
  useEffect(() => {
    const hasStagedFiles = attachments.some((a) => a.file);
    onDirtyChange?.(isFormDirty || hasStagedFiles);
  }, [isFormDirty, attachments, onDirtyChange]);

  // Load existing attachments in edit mode
  useEffect(() => {
    if (existingAttachments && existingAttachments.length > 0) {
      const transformedAttachments = existingAttachments.map((attachment) => ({
        id: attachment.id,
        existingFile: attachment.file,
      }));
      setAttachments(transformedAttachments);
    }
  }, [existingAttachments]);

  const handleDeleteExisting = async (attachmentId: string) => {
    if (!asset?.id || !selectedOrganization?.id) return;
    try {
      await deleteAttachment.mutateAsync({
        organizationId: selectedOrganization.id,
        attachmentId,
        assetId: asset.id,
      });
      // Remove from local state
      setAttachments(prev => prev.filter(a => a.id !== attachmentId));
      // Remove type mapping
      setAttachmentTypes(prev => {
        const updated = { ...prev };
        delete updated[attachmentId];
        return updated;
      });
    } catch (error) {
      console.error('Failed to delete attachment:', error);
    }
  };

  const handleAttachmentsChange = (newAttachments: FileAttachment[]) => {
    setAttachments(newAttachments);
    // Set default type for new files
    const newTypes = { ...attachmentTypes };
    newAttachments.forEach(att => {
      if (!newTypes[att.id] && att.file) {
        // Default to purchase_receipt for new files
        newTypes[att.id] = 'purchase_receipt';
      }
    });
    setAttachmentTypes(newTypes);
  };

  const handleAttachmentTypeChange = (attachmentId: string, type: AssetAttachmentType) => {
    setAttachmentTypes(prev => ({
      ...prev,
      [attachmentId]: type,
    }));
  };

  // Resolve the waste-treatment-method FK we should persist. Returns `null`
  // when the selected EOL plan explicitly does NOT require a method —
  // preventing orphan FKs from a hidden UI field. Falls back to the user's
  // chosen value when the EOL lookup hasn't resolved (we can't confidently
  // strip without knowing what the plan requires).
  const resolveWasteMethodId = (
    endOfLifePlanId: string | undefined,
    wasteMethodId: string | undefined,
  ): string | null => {
    if (!endOfLifePlans) return wasteMethodId || null;
    if (!requiresWasteMethod(endOfLifePlans, endOfLifePlanId)) return null;
    return wasteMethodId || null;
  };

  // The thing's five per-use DEFAULTS as an asset write (every mode). Copied
  // into each new project use by the assign RPC when an item omits the key.
  const thingDefaultsFrom = (values: AssetFormValues) => ({
    unit_code: values.unit_code || null,
    procurement_type_id: values.procurement_type_id || null,
    end_of_life_plan_id: values.end_of_life_plan_id || null,
    waste_treatment_method_id: resolveWasteMethodId(
      values.end_of_life_plan_id,
      values.waste_treatment_method_id,
    ),
    sustainability_notes: values.sustainability_notes || null,
  });

  // Assign freshly created assets. A failure leaves them created but
  // unassigned — say exactly that (the save itself succeeded).
  const assignAfterCreate = async (targetProjectId: string, items: AssignItem[]) => {
    if (!selectedOrganization) return;
    try {
      await assignAssets.mutateAsync({
        organizationId: selectedOrganization.id,
        projectId: targetProjectId,
        items,
        quiet: true,
      });
    } catch {
      toast.error(t("materials.assignAfterCreateFailed"));
    }
  };

  const onSubmit = async (data: FormData) => {
    if (!selectedOrganization) {
      toast.error("No organization selected");
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Set only on a single regular create — used to continue to the new thing's detail
      // page. Edit and purchase (bulk, launched from a project) close in place instead.
      let createdCode: string | null = null;

      if (isPurchaseMode) {
        // Purchase mode logic
        const purchaseData = data as AssetFormValues;
        const quantity = Number.parseInt(purchaseData.quantity ?? "1");
        const purchaseValue = Number.parseFloat(purchaseData.purchase_value ?? "");
        const currentValue = purchaseData.current_value ? Number.parseFloat(purchaseData.current_value) : purchaseValue;

        // Create multiple assets if quantity > 1 — each available and
        // unassigned; ONE assign call below puts them all on the project
        // with the form's per-use values.
        let successCount = 0;
        const failures: { name: string; error: string }[] = [];
        const createdIds: string[] = [];

        for (let i = 0; i < quantity; i++) {
          const assetName = quantity > 1 ? `${purchaseData.name} (${i + 1})` : purchaseData.name;

          const assetData: CreateAssetData = {
            name: assetName,
            description: purchaseData.description || null,
            asset_type_id: purchaseData.asset_type_id,
            status: "available",
            organization_id: selectedOrganization.id,
            purchase_value: purchaseValue,
            current_value: currentValue,
            purchase_date: formatLocalISODate(purchaseData.purchase_date) ?? null,
            location_id: purchaseData.location_id || null,
            business_id: purchaseData.business_id || null,
            purchase_expense_id: purchaseData.purchase_expense_id || null,
            purchase_line_item_id: purchaseData.purchase_line_item_id || null,
            recycled_content_id: purchaseData.recycled_content_id || null,
            weight_kg: purchaseData.weight_kg ?? null,
            // What this project uses becomes the thing's defaults too.
            ...thingDefaultsFrom(purchaseData),
            created_by: user.id,
          };

          try {
            const result = await createAssetMutation.mutateAsync(assetData);
            successCount++;
            if (result?.id) createdIds.push(result.id);

            // Upload attachments for the first asset only in purchase mode (bulk purchase)
            if (i === 0 && result?.id) {
              const filesWithTypes = attachments
                .filter(a => a.file)
                .map(a => ({
                  file: a.file,
                  type: attachmentTypes[a.id] || 'purchase_receipt',
                }));
              if (filesWithTypes.length > 0) {
                await uploadAttachments.mutateAsync({
                  organizationId: selectedOrganization.id,
                  assetId: result.id,
                  filesWithTypes,
                });
              }
            }
          } catch (error) {
            failures.push({ name: assetName, error: error instanceof Error ? error.message : String(error) });
          }
        }

        if (successCount === 0) {
          throw new Error("No assets were created.");
        }

        if (projectId && createdIds.length > 0) {
          const quantityUsed = purchaseData.quantity_used
            ? Number.parseFloat(purchaseData.quantity_used)
            : null;
          const perUse: Omit<AssignItem, "asset_id"> = {
            quantity_used: Number.isFinite(quantityUsed) ? quantityUsed : null,
            unit_code: purchaseData.unit_code || null,
            procurement_type_id: purchaseData.procurement_type_id || null,
            end_of_life_plan_id: purchaseData.end_of_life_plan_id || null,
            waste_treatment_method_id: resolveWasteMethodId(
              purchaseData.end_of_life_plan_id,
              purchaseData.waste_treatment_method_id,
            ),
            sustainability_notes: purchaseData.sustainability_notes || null,
          };
          await assignAfterCreate(projectId, createdIds.map((id) => ({ asset_id: id, ...perUse })));
        }

        if (failures.length) {
          toast.warning(`Created ${successCount}/${quantity} asset(s).`);
        } else {
          toast.successTemplates.created(`${quantity} asset(s)`);
        }
      } else if (isEditMode) {
        // Edit mode
        const assetData = data as AssetFormValues;

        const updateData: UpdateAssetData = {
          id: asset!.id,
          name: assetData.name,
          description: assetData.description || null,
          asset_type_id: assetData.asset_type_id,
          status: assetData.status,
          purchase_value: assetData.purchase_value ? Number.parseFloat(assetData.purchase_value) : null,
          current_value: assetData.current_value ? Number.parseFloat(assetData.current_value) : null,
          purchase_date: formatLocalISODate(assetData.purchase_date) ?? null,
          location_id: assetData.location_id || null,
          business_id: assetData.business_id || null,
          purchase_expense_id: assetData.purchase_expense_id || null,
          purchase_line_item_id: assetData.purchase_line_item_id || null,
          recycled_content_id: assetData.recycled_content_id || null,
          weight_kg: assetData.weight_kg ?? null,
          ...thingDefaultsFrom(assetData),
        };

        await updateAssetMutation.mutateAsync(updateData);

        // Project change → the assignment API: a new project assigns (a
        // transfer when it was on another one); "No project" returns it.
        // The asset itself is already saved, so a refusal here gets its own
        // toast (the coded reason, e.g. a closed project) and the form still
        // closes — never the outer "could not create" toast.
        const nextProjectId = assetData.assigned_project_id || null;
        const currentProjectId = openAssignment?.project_id ?? null;
        try {
          if (nextProjectId && nextProjectId !== currentProjectId) {
            await assignAssets.mutateAsync({
              organizationId: selectedOrganization.id,
              projectId: nextProjectId,
              items: [{ asset_id: asset!.id }],
              quiet: true,
            });
          } else if (!nextProjectId && openAssignment) {
            await returnAssets.mutateAsync({
              organizationId: selectedOrganization.id,
              ids: [openAssignment.id],
              action: "returned",
              quiet: true,
            });
          }
        } catch (projectError) {
          // `quiet` only silences the hook's toasts — App's MutationCache.onError
          // already captured anything that isn't an expected refusal code.
          toastAssignmentError(projectError, "materials.projectChangeFailed");
        }

        // Upload any new attachments
        const filesWithTypes = attachments
          .filter(a => a.file)
          .map(a => ({
            file: a.file,
            type: attachmentTypes[a.id] || 'photo',
          }));
        if (filesWithTypes.length > 0) {
          await uploadAttachments.mutateAsync({
            organizationId: selectedOrganization.id,
            assetId: asset!.id,
            filesWithTypes,
          });
        }
      } else {
        // Regular asset creation mode
        const assetData = data as AssetFormValues;

        const assetPayload: CreateAssetData = {
          name: assetData.name,
          description: assetData.description || null,
          asset_type_id: assetData.asset_type_id,
          status: assetData.status ?? "available",
          organization_id: selectedOrganization.id,
          purchase_value: assetData.purchase_value ? Number.parseFloat(assetData.purchase_value) : null,
          current_value: assetData.current_value ? Number.parseFloat(assetData.current_value) : null,
          purchase_date: formatLocalISODate(assetData.purchase_date) ?? null,
          location_id: assetData.location_id || null,
          business_id: assetData.business_id || null,
          purchase_expense_id: assetData.purchase_expense_id || null,
          purchase_line_item_id: assetData.purchase_line_item_id || null,
          recycled_content_id: assetData.recycled_content_id || null,
          weight_kg: assetData.weight_kg ?? null,
          ...thingDefaultsFrom(assetData),
          created_by: user.id,
        };

        const result = await createAssetMutation.mutateAsync(assetPayload);
        createdCode = result?.code ?? null;

        // Put it on the chosen project through the assignment API.
        if (result?.id && assetData.assigned_project_id) {
          await assignAfterCreate(assetData.assigned_project_id, [{ asset_id: result.id }]);
        }

        // Upload attachments if any new files were added
        const filesWithTypes = attachments
          .filter(a => a.file)
          .map(a => ({
            file: a.file,
            type: attachmentTypes[a.id] || 'photo',
          }));
        if (result?.id && filesWithTypes.length > 0) {
          await uploadAttachments.mutateAsync({
            organizationId: selectedOrganization.id,
            assetId: result.id,
            filesWithTypes,
          });
        }
      }

      form.reset();
      setAttachments([]);
      // Continue to the new thing's detail page on a single create. navigate(replace) runs
      // BEFORE onSuccess (which closes the dialog) so the wrapper skips its history.back().
      if (createdCode) {
        orgNavigate(`/assets/${createdCode}`, { replace: true });
      }
      onSuccess?.();
    } catch (error) {
      console.error("Error creating asset:", error);
      toast.errorTemplates.create(isPurchaseMode ? "purchase" : "asset");
    } finally {
      setLoading(false);
    }
  };

  // Keep the parent-registered submit handler pointing at the latest onSubmit (ref declared above).
  onSubmitRef.current = onSubmit;

  return (
    <Form {...form}>
      <div className="space-y-8">
        {/* Metadata row — classification + source associations, above the document card.
            Status is hidden in purchase mode, so the grid drops to 2 columns there. */}
        <div className={`grid grid-cols-1 gap-4 ${isPurchaseMode ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
          <FormField
            control={form.control}
            name="asset_type_id"
            render={({ field, fieldState }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>{isPurchaseMode ? t("things.form.type") : t("things.form.assetType")}</FormLabel>
                  {fieldState.error && (
                    <span className="text-xs text-destructive">{fieldState.error.message}</span>
                  )}
                </div>
                <FormControl>
                  <DualScopeSelect
                    tableName="asset_types"
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder={isPurchaseMode ? t("things.form.placeholders.selectType") : t("things.form.placeholders.selectAssetType")}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          {!isPurchaseMode && (
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("things.form.status")}</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("things.form.placeholders.selectStatus")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="available">{t("things.form.statuses.available")}</SelectItem>
                      <SelectItem value="in_use">{t("things.form.statuses.inUse")}</SelectItem>
                      <SelectItem value="maintenance">{t("things.form.statuses.maintenance")}</SelectItem>
                      <SelectItem value="retired">{t("things.form.statuses.retired")}</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          )}
          <FormField
            control={form.control}
            name="business_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("things.form.businessOptional")}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t("things.form.placeholders.selectBusiness")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {businesses.map((business) => (
                      <SelectItem key={business.id} value={business.id}>
                        {business.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}
          />
        </div>

        {/* Document body */}
        <Card>
          <CardContent className="pt-6 space-y-8">
            {/* Hero + DETAILS */}
            <div>
              <h2 className="text-[40px] font-extralight leading-tight text-right mb-3 uppercase">
                {t("things.form.heroTitle")}
              </h2>
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <div className="flex items-center gap-2">
                        <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          {isPurchaseMode ? t("things.form.itemName") : t("things.form.name")}
                        </FormLabel>
                        {fieldState.error && (
                          <span className="text-xs text-destructive">{fieldState.error.message}</span>
                        )}
                      </div>
                      <FormControl>
                        <Input
                          placeholder={isPurchaseMode ? t("things.form.placeholders.itemNameExample") : t("things.form.placeholders.assetName")}
                          className={fieldState.error ? "border-destructive" : ""}
                          {...field}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {t("things.form.description")}
                      </FormLabel>
                      <FormControl>
                        <Textarea placeholder={isPurchaseMode ? t("things.form.placeholders.itemDescription") : t("things.form.placeholders.assetDescription")} {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* FINANCIAL */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                {t("things.form.sections.financial")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="purchase_value"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <div className="flex items-center gap-2">
                        <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{isPurchaseMode ? t("things.form.unitPrice") : t("things.form.purchaseValue")}</FormLabel>
                        {fieldState.error && (
                          <span className="text-xs text-destructive">{fieldState.error.message}</span>
                        )}
                      </div>
                      <FormControl>
                        <NumberInput
                          placeholder={t("things.form.placeholders.amount")}
                          allowEmpty
                          className={fieldState.error ? "border-destructive" : ""}
                          {...field}
                          onChange={(value) => field.onChange(String(value))}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                {!isPurchaseMode && (
                  <FormField
                    control={form.control}
                    name="current_value"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("things.form.currentValue")}</FormLabel>
                          {fieldState.error && (
                            <span className="text-xs text-destructive">{fieldState.error.message}</span>
                          )}
                        </div>
                        <FormControl>
                          <NumberInput
                            placeholder={t("things.form.placeholders.amount")}
                            allowEmpty
                            className={fieldState.error ? "border-destructive" : ""}
                            {...field}
                            onChange={(value) => field.onChange(String(value))}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}
                <FormField
                  control={form.control}
                  name="purchase_date"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <div className="flex items-center gap-2">
                        <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("things.form.purchaseDate")}</FormLabel>
                        {fieldState.error && (
                          <span className="text-xs text-destructive">{fieldState.error.message}</span>
                        )}
                      </div>
                      <FormControl>
                        <DatePicker
                          value={field.value}
                          onChange={field.onChange}
                          max={new Date()}
                          className={fieldState.error ? "border-destructive" : ""}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                {isPurchaseMode && (
                  <FormField
                    control={form.control}
                    name="quantity"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <div className="flex items-center gap-2">
                          <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("things.form.quantity")}</FormLabel>
                          {fieldState.error && (
                            <span className="text-xs text-destructive">{fieldState.error.message}</span>
                          )}
                        </div>
                        <FormControl>
                          <NumberInput
                            min={1}
                            step={1}
                            defaultOnBlur={1}
                            className={fieldState.error ? "border-destructive" : ""}
                            {...field}
                            onChange={(value) => field.onChange(String(value))}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </div>

            {/* SUSTAINABILITY & END-OF-LIFE — the one full-bleed tinted band. bg-muted
                (theme-aware) because it embeds DualScopeSelect / UnitSelect / NumberInput. */}
            <div className="-mx-6 px-6 py-6 bg-muted space-y-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                {t("things.form.sustainability")}
              </h3>
              {/* Recycled content (below) is a fact of the thing; the other
                  five are its DEFAULTS for a project use — say which. */}
              <p className="text-xs text-muted-foreground">
                {isPurchaseMode
                  ? t("things.form.projectDefaultsHintPurchase")
                  : t("things.form.projectDefaultsHint")}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="procurement_type_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("things.form.procurementType")}</FormLabel>
                      <FormControl>
                        <DualScopeSelect
                          tableName="procurement_types"
                          value={field.value || undefined}
                          onValueChange={field.onChange}
                          placeholder={t("things.form.placeholders.selectProcurementType")}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="recycled_content_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("things.form.recycledContent")}</FormLabel>
                      <FormControl>
                        <DualScopeSelect
                          tableName="recycled_content_types"
                          value={field.value || undefined}
                          onValueChange={field.onChange}
                          placeholder={t("things.form.placeholders.selectRecycledContent")}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              {/* The thing's per-use DEFAULTS (every mode): copied into each new
                  project use. In purchase mode they are also this project's
                  use (sent with the assign call). */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="end_of_life_plan_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("things.form.endOfLifePlan")}</FormLabel>
                      <FormControl>
                        <DualScopeSelect
                          tableName="end_of_life_plans"
                          value={field.value || undefined}
                          onValueChange={(newPlanId) => {
                            field.onChange(newPlanId);
                            // Skip the auto-clear if lookup data hasn't resolved —
                            // we'd otherwise treat "unknown" as "does not require"
                            // and silently wipe a valid field.
                            if (!endOfLifePlans) return;
                            // If the new EOL plan does not require a waste method,
                            // clear any previously-picked method so we don't submit
                            // an orphan FK.
                            if (!requiresWasteMethod(endOfLifePlans, newPlanId)) {
                              form.setValue("waste_treatment_method_id", "", { shouldDirty: true });
                            }
                          }}
                          placeholder={t("things.form.placeholders.selectEndOfLifePlan")}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                {requiresWasteMethod(
                  endOfLifePlans,
                  form.watch("end_of_life_plan_id") as string | undefined,
                ) && (
                  <FormField
                    control={form.control}
                    name="waste_treatment_method_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("things.form.wasteTreatmentMethod")}</FormLabel>
                        <FormControl>
                          <DualScopeSelect
                            tableName="waste_treatment_methods"
                            value={field.value || undefined}
                            onValueChange={field.onChange}
                            placeholder={t("things.form.placeholders.selectWasteTreatmentMethod")}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="weight_kg"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <div className="flex items-center gap-2">
                        <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("things.form.weightKg")}</FormLabel>
                        {fieldState.error && (
                          <span className="text-xs text-destructive">{fieldState.error.message}</span>
                        )}
                      </div>
                      <FormControl>
                        <NumberInput
                          placeholder={t("things.form.placeholders.weightKg")}
                          allowEmpty
                          min={0}
                          className={fieldState.error ? "border-destructive" : ""}
                          name={field.name}
                          ref={field.ref}
                          onBlur={field.onBlur}
                          value={(field.value as number | string | undefined) ?? ""}
                          onChange={(value) => field.onChange(value === "" ? undefined : value)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="unit_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("things.form.unit")}</FormLabel>
                      <FormControl>
                        <UnitSelect
                          value={field.value || undefined}
                          onValueChange={field.onChange}
                          placeholder={t("things.form.placeholders.selectUnit")}
                          ariaLabel={t("things.form.unit")}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="sustainability_notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("things.form.sustainabilityNotes")}</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t("things.form.placeholders.sustainabilityNotesHint")}
                        {...field}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            {/* LINKS */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                {t("things.form.sections.links")}
              </h3>
              <div className={`grid grid-cols-1 gap-4 ${isPurchaseMode ? "md:grid-cols-1" : "md:grid-cols-2"}`}>
                <FormField
                  control={form.control}
                  name="location_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{isPurchaseMode ? t("things.form.storagePlaceOptional") : t("things.form.placeOptional")}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t("things.form.placeholders.selectPlace")} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {locations.map((location) => (
                            <SelectItem key={location.id} value={location.id}>
                              {location.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
                {!isPurchaseMode && (
                  <FormField
                    control={form.control}
                    name="assigned_project_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("things.form.assignedProjectOptional")}</FormLabel>
                        {/* Edit: wait for the assignment history — the open
                            assignment, not the stored pointer, is the truth. */}
                        <Select
                          onValueChange={(value) => field.onChange(value === NO_PROJECT ? "" : value)}
                          value={field.value || NO_PROJECT}
                          disabled={isEditMode && historyLoading}
                        >
                          <FormControl>
                            <SelectTrigger>
                              {/* Never empty (falls back to NO_PROJECT), so no placeholder. */}
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value={NO_PROJECT}>{t("things.form.noProject")}</SelectItem>
                            {projects.map((project) => (
                              <SelectItem key={project.id} value={project.id}>
                                {project.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                )}
              </div>
              {/* Purchase-expense link: accounting data, `accounting` scope only. */}
              {canLinkExpense && (
                <div className="mt-4 space-y-2">
                  <FormLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("things.form.linkToExpense")}</FormLabel>
                  <ExpenseLinkingSection
                    selectedExpenseId={form.watch("purchase_expense_id")}
                    selectedLineItemId={form.watch("purchase_line_item_id")}
                    onExpenseChange={(expenseId) => form.setValue("purchase_expense_id", expenseId)}
                    onLineItemChange={(lineItemId) => form.setValue("purchase_line_item_id", lineItemId)}
                    currentAssetId={asset?.id}
                  />
                </div>
              )}
            </div>

            {/* ATTACHMENTS */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                {t("things.form.attachments")}
              </h3>
              <Card>
                <CardContent className="p-4 space-y-3">
                  <FileAttachments
                    value={attachments}
                    onChange={handleAttachmentsChange}
                    onDeleteExisting={handleDeleteExisting}
                    disabled={loading}
                    maxFiles={10}
                  />

                  {/* Show type selectors for each new file */}
                  {attachments.filter(a => a.file).length > 0 && (
                    <div className="space-y-2 pt-2 border-t">
                      <div className="text-sm font-medium">{t("things.form.attachmentTypes")}</div>
                      {attachments.filter(a => a.file).map((attachment) => (
                        <div key={attachment.id} className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground flex-1 truncate" title={attachment.file.name}>
                            {attachment.file.name}
                          </span>
                          <Select
                            value={attachmentTypes[attachment.id] || 'purchase_receipt'}
                            onValueChange={(value) => handleAttachmentTypeChange(attachment.id, value as AssetAttachmentType)}
                          >
                            <SelectTrigger className="w-[180px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="purchase_receipt">{t("things.form.attachmentTypeOptions.purchaseReceipt")}</SelectItem>
                              <SelectItem value="manual">{t("things.form.attachmentTypeOptions.manual")}</SelectItem>
                              <SelectItem value="warranty">{t("things.form.attachmentTypeOptions.warranty")}</SelectItem>
                              <SelectItem value="photo">{t("things.form.attachmentTypeOptions.photo")}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

          </CardContent>
        </Card>
      </div>
    </Form>
  );
}
